import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import GlobalSearch from './GlobalSearch';
import * as FiIcons from 'react-icons/fi';

const { FiGrid, FiHelpCircle, FiHome, FiList, FiLogOut, FiMenu, FiUser, FiVideo, FiX } = FiIcons;
const BPA_LOGO_URL = 'https://cloud.sharemyimage.com/2026/07/26/logo_fixed_512_true_png.png';

const Layout = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [signingOut, setSigningOut] = React.useState(false);

  const handleLogout = () => {
    if (signingOut) return;
    setSigningOut(true);

    window.setTimeout(() => {
      logout();
      navigate('/login', { replace: true });
    }, 240);
  };

  const getDisplayName = (currentUser) => {
    if (!currentUser) return 'Guest';
    if (currentUser.name) return currentUser.name;
    if (currentUser.full_name) return currentUser.full_name;
    if (currentUser.email) {
      const namePart = currentUser.email.split('@')[0];
      return namePart
        .split(/[._]/)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
    return 'Admin User';
  };

  const navigation = [
    { name: 'Dashboard', href: '/', icon: FiHome },
    { name: 'Apps', href: '/apps', icon: FiGrid },
    { name: 'Videos', href: '/videos', icon: FiVideo },
    { name: 'Topics & Playlists', href: '/playlists', icon: FiList },
    { name: 'Help & FAQ', href: '/help', icon: FiHelpCircle }
  ];

  return (
    <div className="app-shell">
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="app-backdrop fixed inset-0 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } app-sidebar fixed inset-y-0 left-0 z-30 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}
      >
        <div className="app-sidebar-header flex items-center justify-between h-16 px-5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="bpa-brand-logo-wrap" title="BPA Solutions">
              <img src={BPA_LOGO_URL} alt="BPA Solutions" className="bpa-brand-logo" />
            </div>
            <div className="min-w-0">
              <p className="app-brand-title text-[15px] font-bold tracking-tight truncate">BPA Video Hub</p>
              <p className="app-subtle text-[10px] uppercase tracking-[0.16em] mt-0.5">Admin Console</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-lg app-muted hover:opacity-70"
            aria-label="Close navigation"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <nav className="mt-7 px-4 space-y-1.5" aria-label="Primary navigation">
          {navigation.map(item => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href === '/'}
              className={({ isActive }) =>
                `app-nav-link ${isActive ? 'app-nav-link-active' : ''} flex items-center px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-200`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <SafeIcon icon={item.icon} className="w-5 h-5 mr-3 flex-shrink-0" />
              <span>{item.name}</span>
            </NavLink>
          ))}
        </nav>

        <div className="app-user-panel absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center mb-3 px-2">
            <div className="app-avatar w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <SafeIcon icon={FiUser} className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="app-brand-title text-sm font-semibold truncate">{getDisplayName(user)}</p>
              <p className="app-subtle text-xs truncate mt-0.5">{user?.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            disabled={signingOut}
            className="sign-out-button flex items-center w-full px-4 py-2.5 text-sm font-semibold rounded-lg disabled:opacity-60"
          >
            <SafeIcon icon={FiLogOut} className="w-4 h-4 mr-3" />
            {signingOut ? 'Signing out…' : 'Sign Out'}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 app-page">
        <header className="app-mobile-header lg:hidden flex items-center justify-between h-16 px-4">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg app-muted hover:opacity-70"
            aria-label="Open navigation"
          >
            <SafeIcon icon={FiMenu} className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="bpa-brand-logo-wrap bpa-brand-logo-wrap-mobile">
              <img src={BPA_LOGO_URL} alt="BPA Solutions" className="bpa-brand-logo" />
            </div>
            <span className="app-brand-title text-sm font-bold">Video Hub</span>
          </div>
          <div className="w-10" aria-hidden="true" />
        </header>

        <div className="px-4 lg:px-8 pt-4 lg:pt-6">
          <GlobalSearch />
        </div>

        <main className="app-page flex-1 overflow-auto p-4 lg:p-8">
          <Outlet />
        </main>
      </div>

      {signingOut && (
        <div className="auth-exit-overlay" role="status" aria-live="polite">
          <div className="auth-exit-card">
            <div className="auth-spinner" aria-hidden="true" />
            <p className="font-semibold">Signing out securely…</p>
            <p className="app-subtle text-xs mt-1">Closing your admin session</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;